import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
} from '@mui/material';
import OutboxIcon from '@mui/icons-material/Outbox';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import DraftsIcon from '@mui/icons-material/Drafts';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useEmail } from '../../hooks/useEmail';
import useEmailBounceState from '../../hooks/useEmailBounceState';
import AuthContext from '../../context/authContext';
import { getRenderableEmailHtml, hasRenderableEmailContent } from '../../util/emailContent';

const renderDeliveryStatusChip = mail => {
  if (!mail) return null;

  if (mail.direction === 'inbound') {
    return (
      <Chip
        size='small'
        color='success'
        icon={<MoveToInboxIcon />}
        label='Eingang'
        sx={{ height: 22 }}
      />
    );
  }

  const status = mail.status;
  if (status === 'failed' || mail.deliveryStatus?.failedAt) {
    return (
      <Tooltip
        title={`Bounce${
          mail.deliveryStatus?.failedAt
            ? ` (${dayjs(mail.deliveryStatus.failedAt).format('DD.MM.YYYY HH:mm')})`
            : ''
        }${mail.deliveryStatus?.errorMessage ? `: ${mail.deliveryStatus.errorMessage}` : ''}`}
      >
        <Chip
          size='small'
          color='error'
          icon={<ErrorOutlineIcon />}
          label='Bounce'
          sx={{ height: 22 }}
        />
      </Tooltip>
    );
  }
  if (status === 'opened') {
    return (
      <Tooltip
        title={
          mail.deliveryStatus?.openedAt
            ? `Geöffnet am ${dayjs(mail.deliveryStatus.openedAt).format('DD.MM.YYYY HH:mm')}`
            : 'Geöffnet'
        }
      >
        <Chip
          size='small'
          color='success'
          icon={<DraftsIcon />}
          label='Geöffnet'
          sx={{ height: 22 }}
        />
      </Tooltip>
    );
  }
  if (status === 'delivered') {
    return (
      <Tooltip
        title={
          mail.deliveryStatus?.deliveredAt
            ? `Zugestellt am ${dayjs(mail.deliveryStatus.deliveredAt).format('DD.MM.YYYY HH:mm')}`
            : 'Zugestellt'
        }
      >
        <Chip
          size='small'
          color='success'
          variant='outlined'
          icon={<MarkEmailReadIcon />}
          label='Zugestellt'
          sx={{ height: 22 }}
        />
      </Tooltip>
    );
  }
  if (status === 'sent') {
    return (
      <Tooltip
        title={
          mail.deliveryStatus?.sentAt
            ? `Versendet am ${dayjs(mail.deliveryStatus.sentAt).format('DD.MM.YYYY HH:mm')} – noch keine Bestätigung`
            : 'Versendet'
        }
      >
        <Chip
          size='small'
          variant='outlined'
          icon={<ScheduleSendIcon />}
          label='Versendet'
          sx={{ height: 22 }}
        />
      </Tooltip>
    );
  }
  return (
    <Chip size='small' variant='outlined' label={status || '—'} sx={{ height: 22 }} />
  );
};

const getMailDate = mail => {
  if (!mail) return null;
  if (mail.direction === 'inbound') {
    return mail.receivedAt || mail.meta?.createdAt || null;
  }
  return (
    mail.deliveryStatus?.deliveredAt ||
    mail.deliveryStatus?.sentAt ||
    mail.meta?.createdAt ||
    null
  );
};

const InvoiceMailHistoryModal = ({ open, onClose, invoice }) => {
  const authCtx = useContext(AuthContext);
  const canClearBounce = authCtx.checkRight('emailBounceState:clear');
  const { fetchEmailsByInvoiceId, isLoading } = useEmail();
  const { fetchByEmail, clearBounce } = useEmailBounceState();
  const [mails, setMails] = useState([]);
  const [previewMail, setPreviewMail] = useState(null);
  const [bounceState, setBounceState] = useState(null);
  const [clearing, setClearing] = useState(false);
  const clearReasonRef = useRef(null);
  const recipientEmail = invoice?.customerData?.mail || null;

  const reloadBounceState = useCallback(async () => {
    if (!recipientEmail) {
      setBounceState(null);
      return;
    }
    try {
      const state = await fetchByEmail(recipientEmail);
      setBounceState(state || null);
    } catch (e) {
      setBounceState(null);
    }
  }, [recipientEmail, fetchByEmail]);

  useEffect(() => {
    if (!open || !invoice?._id) {
      setMails([]);
      setPreviewMail(null);
      setBounceState(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetchEmailsByInvoiceId(invoice._id);
        if (cancelled) return;
        setMails(response?.mails || []);
      } catch (e) {
        if (cancelled) return;
        toast.error('Fehler beim Laden des Mailverlaufs.');
        setMails([]);
      }
      if (recipientEmail) {
        try {
          const state = await fetchByEmail(recipientEmail);
          if (!cancelled) setBounceState(state || null);
        } catch (e) {
          if (!cancelled) setBounceState(null);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // fetchEmailsByInvoiceId / fetchByEmail are intentionally omitted: useHttp
    // hooks return new function references on every render, which would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice?._id, recipientEmail]);

  const handleClearBounce = useCallback(async () => {
    if (!recipientEmail) return;
    setClearing(true);
    try {
      const reason = clearReasonRef.current?.value?.trim() || null;
      await clearBounce(recipientEmail, reason);
      toast.success('Adressensperre aufgehoben.');
      await reloadBounceState();
    } catch (e) {
      toast.error('Fehler beim Aufheben der Adressensperre.');
    } finally {
      setClearing(false);
    }
  }, [recipientEmail, clearBounce, reloadBounceState]);

  const sortedMails = useMemo(() => {
    const arr = Array.isArray(mails) ? [...mails] : [];
    arr.sort((a, b) => {
      const da = new Date(getMailDate(a) || 0).getTime();
      const db = new Date(getMailDate(b) || 0).getTime();
      return db - da;
    });
    return arr;
  }, [mails]);

  const headerLine = invoice?.invoiceNumber
    ? `Mailverlauf zu Rechnung ${invoice.invoiceNumber}`
    : 'Mailverlauf';

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
        <DialogTitle>{headerLine}</DialogTitle>
        <DialogContent dividers>
          {bounceState?.isSuppressed && (
            <Alert
              severity='error'
              icon={<ErrorOutlineIcon />}
              sx={{ mb: 2 }}
              action={
                canClearBounce ? (
                  <Button
                    size='small'
                    color='inherit'
                    startIcon={<LockOpenIcon />}
                    onClick={handleClearBounce}
                    disabled={clearing}
                  >
                    {clearing ? 'Wird aufgehoben…' : 'Sperre aufheben'}
                  </Button>
                ) : null
              }
            >
              <AlertTitle>Adresse als nicht zustellbar gesperrt</AlertTitle>
              <Typography variant='body2'>
                <b>{recipientEmail}</b>
                {bounceState.lastHardBounceAt
                  ? ` – seit ${dayjs(bounceState.lastHardBounceAt).format('DD.MM.YYYY HH:mm')}`
                  : ''}
                {bounceState.lastBounceType ? ` (${bounceState.lastBounceType})` : ''}
              </Typography>
              {bounceState.lastErrorMessage && (
                <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.5 }}>
                  {bounceState.lastErrorMessage}
                </Typography>
              )}
              {canClearBounce && (
                <TextField
                  size='small'
                  fullWidth
                  inputRef={clearReasonRef}
                  placeholder='Optional: Grund für die Aufhebung (z.B. Adresse korrigiert)'
                  sx={{ mt: 1.5 }}
                />
              )}
            </Alert>
          )}
          {!bounceState?.isSuppressed && bounceState?.softBounceCount > 0 && (
            <Alert severity='warning' sx={{ mb: 2 }}>
              <Typography variant='body2'>
                Adresse hat {bounceState.softBounceCount} Soft-Bounce(s)
                {bounceState.lastSoftBounceAt
                  ? ` (zuletzt am ${dayjs(bounceState.lastSoftBounceAt).format('DD.MM.YYYY HH:mm')})`
                  : ''}
                .
              </Typography>
            </Alert>
          )}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!isLoading && sortedMails.length === 0 && (
            <Typography variant='body2' color='text.secondary' sx={{ py: 2 }}>
              Für diese Rechnung wurden noch keine E-Mails versendet oder empfangen.
            </Typography>
          )}
          {!isLoading && sortedMails.length > 0 && (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell sx={{ width: 160 }}>Datum</TableCell>
                    <TableCell>Betreff / Vorlage</TableCell>
                    <TableCell sx={{ width: 220 }}>Empfänger</TableCell>
                    <TableCell sx={{ width: 130 }}>Status</TableCell>
                    <TableCell sx={{ width: 60 }} align='right'>
                      Aktion
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedMails.map(mail => {
                    const date = getMailDate(mail);
                    const isOutbound = mail.direction === 'outbound';
                    return (
                      <TableRow key={mail._id} hover>
                        <TableCell>
                          {isOutbound ? (
                            <OutboxIcon color='primary' fontSize='small' />
                          ) : (
                            <MoveToInboxIcon color='success' fontSize='small' />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2' sx={{ fontWeight: 500 }}>
                            {mail.subject || '(kein Betreff)'}
                          </Typography>
                          {mail.templateKey && (
                            <Typography variant='caption' color='text.secondary'>
                              {mail.templateKey}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {isOutbound ? mail.to : mail.from}
                          </Typography>
                        </TableCell>
                        <TableCell>{renderDeliveryStatusChip(mail)}</TableCell>
                        <TableCell align='right'>
                          {hasRenderableEmailContent(mail) && (
                            <Tooltip title='Inhalt anzeigen'>
                              <IconButton size='small' onClick={() => setPreviewMail(mail)}>
                                <VisibilityIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(previewMail)}
        onClose={() => setPreviewMail(null)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {previewMail?.subject || '(kein Betreff)'}
          <Box sx={{ mt: 0.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant='caption' color='text.secondary'>
              {previewMail?.direction === 'inbound'
                ? `Von ${previewMail?.from || '—'}`
                : `An ${previewMail?.to || '—'}`}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              ·
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {previewMail && getMailDate(previewMail)
                ? dayjs(getMailDate(previewMail)).format('DD.MM.YYYY HH:mm')
                : ''}
            </Typography>
            {renderDeliveryStatusChip(previewMail)}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {hasRenderableEmailContent(previewMail) ? (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                maxHeight: '70vh',
                overflow: 'auto',
              }}
              dangerouslySetInnerHTML={{ __html: getRenderableEmailHtml(previewMail) }}
            />
          ) : (
            <Box
              component='pre'
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'inherit',
                fontSize: '0.875rem',
                p: 0,
                m: 0,
              }}
            >
              {previewMail?.textBody || '(Kein Inhalt verfügbar)'}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewMail(null)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InvoiceMailHistoryModal;
